import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { createElement } from "react";
import { auth } from "../services/api";
import type { User } from "../types";

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = "zr_token";
const USER_KEY = "zr_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    let user: User | null = null;

    if (userJson) {
      try {
        user = JSON.parse(userJson) as User;
      } catch {
        localStorage.removeItem(USER_KEY);
      }
    }

    return {
      token,
      user,
      isLoading: !!token,
      isAuthenticated: !!token && !!user,
    };
  });

  const login = useCallback((token: string, user: User) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setState({
      token,
      user,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setState({
      token: null,
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user } = await auth.getMe();
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setState((prev) => ({ ...prev, user, isAuthenticated: true }));
    } catch {
      logout();
    }
  }, [logout]);

  // On mount, if we have a token, validate it by fetching the user
  useEffect(() => {
    if (state.token && state.isLoading) {
      auth
        .getMe()
        .then(({ user }) => {
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          setState({
            token: state.token,
            user,
            isLoading: false,
            isAuthenticated: true,
          });
        })
        .catch(() => {
          logout();
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshUser,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
